import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  HolidayBulkImportRequest,
  HolidayBulkImportResponse,
  HolidayKind,
  HolidayList,
  HolidayPayload,
  HolidaySuggestionList,
  HolidayView,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase G3.5 FE-2 — IA holiday hooks.
 *
 * Backed by the BE-3 admin/calendar routes:
 *   GET    /admin/calendar/holidays
 *   GET    /admin/calendar/holidays/{id}
 *   POST   /admin/calendar/holidays
 *   PUT    /admin/calendar/holidays/{id}
 *   DELETE /admin/calendar/holidays/{id}
 *   GET    /admin/calendar/holiday-suggestions
 *   POST   /admin/calendar/holidays:bulk-import
 *
 * Stale window: 60s for the listing (holidays change rarely once
 * authored) and ~indefinite for suggestions (the India catalogue is
 * static within a year). Mutations bust the relevant cache via
 * `invalidateQueries`.
 */

const CALENDAR_ROOT = "/admin/calendar";

// ── Query keys ───────────────────────────────────────────────────────

export const holidayKeys = {
  all: ["calendar"] as const,
  holidays: () => [...holidayKeys.all, "holidays"] as const,
  list: (filters: HolidayFilters) => [...holidayKeys.holidays(), "list", filters] as const,
  detail: (id: string) => [...holidayKeys.holidays(), "detail", id] as const,
  suggestions: () => [...holidayKeys.all, "suggestions"] as const,
};

// ── Filters ──────────────────────────────────────────────────────────

export interface HolidayFilters {
  branchId?: string | null;
  kind?: HolidayKind | null;
  /** `YYYY-MM-DD`. Holidays whose `start_date >= dateFrom`. */
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Default `true` — surface tenant-wide rows alongside branch
   * rows. Set `false` to inspect ONLY a branch's overrides. */
  includeTenantWide?: boolean;
  limit?: number;
  offset?: number;
}

function buildHolidaysQuery(filters: HolidayFilters): string {
  const params = new URLSearchParams();
  if (filters.branchId) params.set("branch_id", filters.branchId);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.includeTenantWide === false) {
    params.set("include_tenant_wide", "false");
  }
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  return params.toString();
}

// ── Reads ────────────────────────────────────────────────────────────

export function useHolidays(filters: HolidayFilters = {}) {
  return useQuery<HolidayList>({
    queryKey: holidayKeys.list(filters),
    queryFn: () => {
      const query = buildHolidaysQuery(filters);
      const url = query ? `${CALENDAR_ROOT}/holidays?${query}` : `${CALENDAR_ROOT}/holidays`;
      return api.get<HolidayList>(url);
    },
    staleTime: 60_000,
  });
}

export function useHolidaySuggestions() {
  return useQuery<HolidaySuggestionList>({
    queryKey: holidayKeys.suggestions(),
    queryFn: () => api.get<HolidaySuggestionList>(`${CALENDAR_ROOT}/holiday-suggestions`),
    // The catalogue is effectively static within an academic year;
    // a 5-minute stale window is plenty.
    staleTime: 5 * 60_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────

export function useCreateHoliday() {
  const queryClient = useQueryClient();
  return useMutation<HolidayView, Error, HolidayPayload>({
    mutationFn: (payload) => api.post<HolidayView>(`${CALENDAR_ROOT}/holidays`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: holidayKeys.holidays() });
    },
  });
}

export function useUpdateHoliday() {
  const queryClient = useQueryClient();
  return useMutation<
    HolidayView,
    Error,
    {
      holidayId: string;
      payload: HolidayPayload;
    }
  >({
    mutationFn: ({ holidayId, payload }) =>
      api.put<HolidayView>(`${CALENDAR_ROOT}/holidays/${encodeURIComponent(holidayId)}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: holidayKeys.holidays() });
    },
  });
}

export function useDeleteHoliday() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { holidayId: string }>({
    mutationFn: ({ holidayId }) =>
      api.delete<void>(`${CALENDAR_ROOT}/holidays/${encodeURIComponent(holidayId)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: holidayKeys.holidays() });
    },
  });
}

export function useBulkImportHolidaySuggestions() {
  const queryClient = useQueryClient();
  return useMutation<HolidayBulkImportResponse, Error, HolidayBulkImportRequest>({
    mutationFn: (payload) =>
      api.post<HolidayBulkImportResponse>(`${CALENDAR_ROOT}/holidays:bulk-import`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: holidayKeys.holidays() });
    },
  });
}
